'use client';

import { useEffect, useContext, useState, type FC, ChangeEvent } from 'react';
import { TaxReturnSendDialogProps } from './TaxReturnSendDialog.types';
import {
  Button,
  Text,
  Input,
  Toggle,
  Flex,
  Dialog,
  TrixEditor,
} from '@/_components';
import { sendTaxReturnToDocuments } from '@/_api/admin-tax-returns';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { FirmSettingsContext } from '@/_contexts/FirmSettingsProvider';
import { AdminTaxReturnsContext } from '@/_contexts/admin/AdminTaxReturnsProvider';

const DEFAULT_SUBJECT =
  'Your {tax_year} tax return is now accessible in Documents Portal.';
const DEFAULT_MESSAGE =
  "Hello {client_preferred_name},<br/><br/>We're pleased to inform you that your {tax_year} tax return is now available for access through the Documents Portal. Please click the button below to view and download your tax return.<br/><br/>Best regards";

export const TaxReturnSendDialog: FC<TaxReturnSendDialogProps> = () => {
  const { preferences, reloadFirm } = useContext(FirmSettingsContext);

  const [isLoading, setLoading] = useState<boolean>(false);
  const [subject, setSubject] = useState<string>(DEFAULT_SUBJECT);
  const [message, setMessage] = useState<string>(DEFAULT_MESSAGE);
  const [documentName, setDocumentName] = useState<string>('');
  const [path, setPath] = useState<string>('');
  const [saveSubjectForFutureUse, setSaveSubjectForFutureUse] =
    useState<boolean>(false);
  const [saveMessageForFutureUse, setSaveMessageForFutureUse] =
    useState<boolean>(false);

  // Watermark is enabled by default unless explicitly disabled via preference
  const defaultWatermarkEnabled =
    !preferences.tax_return_send_watermark_default_off;
  const [watermarkEnabled, setWatermarkEnabled] = useState<boolean>(
    defaultWatermarkEnabled,
  );

  const { selectedTaxReturnExport, setSelectedTaxReturnExport } = useContext(
    AdminTaxReturnsContext,
  );

  const onClose = () => {
    setSelectedTaxReturnExport(undefined);
    setWatermarkEnabled(defaultWatermarkEnabled);
  };

  useEffect(() => {
    setDocumentName(selectedTaxReturnExport?.tax_return_name || '');

    const path =
      selectedTaxReturnExport && preferences.tax_return_send_to_documents_path
        ? preferences.tax_return_send_to_documents_path.replace(
            '{year}',
            selectedTaxReturnExport.tax_year.toString(),
          )
        : `/${selectedTaxReturnExport?.tax_year}/Tax Return` || '';

    setPath(path);

    setSaveMessageForFutureUse(false);
    setSaveSubjectForFutureUse(false);
  }, [selectedTaxReturnExport, preferences]);

  useEffect(() => {
    setSubject(
      preferences.send_tax_return_to_documents_default_subject ||
        DEFAULT_SUBJECT,
    );
    setMessage(
      preferences.send_tax_return_to_documents_default_message ||
        DEFAULT_MESSAGE,
    );
    setWatermarkEnabled(defaultWatermarkEnabled);
  }, [selectedTaxReturnExport?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);

  const onSubjectChange = (event: ChangeEvent<HTMLInputElement>) =>
    setSubject(event.target.value);
  const onDocumentNameChange = (event: ChangeEvent<HTMLInputElement>) =>
    setDocumentName(event.target.value);
  const onSaveSubjectToggle = (_event: ChangeEvent<HTMLInputElement>) =>
    setSaveSubjectForFutureUse((previous) => !previous);
  const onSaveMessageToggle = (_event: ChangeEvent<HTMLInputElement>) =>
    setSaveMessageForFutureUse((previous) => !previous);
  const onWatermarkToggle = (_event: ChangeEvent<HTMLInputElement>) =>
    setWatermarkEnabled((previous) => !previous);

  const onSend = async () => {
    if (!selectedTaxReturnExport?.id) return;

    try {
      setLoading(true);

      const { error } = await sendTaxReturnToDocuments({
        token,
        id: selectedTaxReturnExport.id,
        companyId: selectedTaxReturnExport.company_id,
        subject,
        message,
        document_name: documentName,
        path,
        save_subject_for_future_use: saveSubjectForFutureUse,
        save_message_for_future_use: saveMessageForFutureUse,
        watermark_enabled: watermarkEnabled,
      });

      if (!error) {
        addToast('success', 'Sent to documents successfully');
      }

      if (saveSubjectForFutureUse || saveMessageForFutureUse) {
        reloadFirm();
      }
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoading(false);
      onClose();
    }
  };

  return (
    <Dialog
      isOpen={!!selectedTaxReturnExport?.id}
      onClose={onClose}
      isWide={true}
      title="Send Tax Return to Documents"
    >
      <Dialog.Content>
        <Flex isColumn={true} gap={12}>
          <Flex isColumn={true} gap={4}>
            <Input
              value={subject}
              onChange={onSubjectChange}
              isDisabled={isLoading}
              label="Subject"
              placeholder="Subject"
            />
            <Toggle
              isChecked={saveSubjectForFutureUse}
              isSmall={true}
              isDisabled={isLoading}
              label="Save for future use"
              onChange={onSaveSubjectToggle}
            />
          </Flex>
          <Flex isColumn={true} gap={4}>
            <TrixEditor
              isDisabled={isLoading}
              value={message}
              setValue={setMessage}
            />
            <Toggle
              isChecked={saveMessageForFutureUse}
              isDisabled={isLoading}
              isSmall={true}
              label="Save for future use"
              onChange={onSaveMessageToggle}
            />
          </Flex>
          <Input
            value={documentName}
            onChange={onDocumentNameChange}
            isDisabled={isLoading}
            label="Document Name"
            placeholder="Document Name"
          />
          <Text type="small" fontWeight="medium">
            Path: {path}/{documentName}
          </Text>
        </Flex>
      </Dialog.Content>
      <Dialog.Footer>
        <Flex xAlign="end" isYCentered gap={6}>
          <Toggle
            isChecked={watermarkEnabled}
            isDisabled={isLoading}
            label="Watermark"
            onChange={onWatermarkToggle}
            isReverse
          />
          <Button
            value="Cancel"
            onClick={onClose}
            intent="secondary"
            isDisabled={isLoading}
          />
          <Button
            value="Send"
            onClick={onSend}
            intent="primary"
            isDisabled={isLoading}
            isLoading={isLoading}
          />
        </Flex>
      </Dialog.Footer>
    </Dialog>
  );
};
